/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { FunctionComponent, ReactNode, useState } from 'react';
import {
  type EuiBreadcrumb,
  EuiHeaderBreadcrumbs,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopover,
} from '@elastic/eui';
import { BurgerNav } from './BurgerNav';
import { useUiChrome } from './useUiChrome';

export type ToolbarProps = {
  breadcrumb?: EuiBreadcrumb[];
  actions?: ReactNode[];
};

/**
 * Secondary action toolbar, on the page background below the brand bar (the
 * blue TopBar is branding only). Left: burger toggle +
 * breadcrumbs. Right: surface-supplied actions — inline on desktop, collapsed
 * into an overflow menu on overlay/phone breakpoints.
 */
export const Toolbar: FunctionComponent<ToolbarProps> = ({
  breadcrumb,
  actions,
}) => {
  const { isOverlay, isActionsOverflow, isDocked } = useUiChrome();
  const [isActionsOpen, setActionsOpen] = useState(false);

  const crumbs: EuiBreadcrumb[] =
    breadcrumb && breadcrumb.length ? breadcrumb : [{ text: 'Home' }];
  const hasActions = actions != null && actions.length > 0;

  return (
    <EuiFlexGroup
      alignItems="center"
      gutterSize="m"
      responsive={false}
      css={{ marginBottom: 16 }}>
      {/*
        When docked the burger toggle is hidden, but its flex slot + gutter would
        still offset the breadcrumbs from the content's left edge. Take the slot
        out of flex flow with position:absolute rather than display:none — the
        docked rail is a fixed-positioned DOM descendant, so display:none hid it
        too, whereas an absolute ancestor neither moves nor hides a fixed child.
      */}
      <EuiFlexItem
        grow={false}
        css={isDocked ? { position: 'absolute', inlineSize: 0 } : undefined}>
        <BurgerNav />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiHeaderBreadcrumbs
          breadcrumbs={crumbs}
          responsive={isOverlay ? undefined : false}
          truncate={false}
          css={{ marginLeft: '0' }}
        />
      </EuiFlexItem>

      {/* spacer pushes the actions to the right */}
      <EuiFlexItem />

      {hasActions &&
        (isActionsOverflow ? (
          <EuiFlexItem grow={false}>
            <EuiPopover
              anchorPosition="downRight"
              isOpen={isActionsOpen}
              closePopover={() => setActionsOpen(false)}
              panelPaddingSize="none"
              button={
                <EuiButtonIcon
                  aria-label="More actions"
                  iconType="boxesVertical"
                  display="base"
                  size="s"
                  onClick={() => setActionsOpen(open => !open)}
                />
              }>
              {/*
                No onClickCapture-to-close here: closing the parent on any inner
                click also tore down nested overlays (the period select, the Add
                Activity sub-popover), so they couldn't be used. The menu now
                closes on outside-click / navigation instead.
              */}
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: '8px',
                  padding: '8px',
                  minInlineSize: '220px',
                  // Stacked actions go full-width: force buttons / selects / the
                  // Add-Activity sub-popover anchor / segmented toggles to fill
                  // the column so every row is a consistent width.
                  '.euiButton, .euiButtonEmpty, .euiPopover, .euiPopover__anchor, .euiSuperSelectControl, .euiFormControlLayout, .euiButtonGroup, .euiButtonGroup__buttons':
                    { inlineSize: '100%', maxInlineSize: '100%' },
                  // A surface may hand us a *grouped* action — e.g. the campaign
                  // header's Clone / Delete / Edit in a single horizontal
                  // EuiFlexGroup. Collapse any nested group to a vertical stack
                  // so its buttons fill the column instead of running off the
                  // panel edge. (Segmented EuiButtonGroups are exempt — they're
                  // not EuiFlexGroups, so they stay horizontal as intended.)
                  '.euiFlexGroup': {
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: '8px',
                  },
                  '.euiFlexGroup > .euiFlexItem': { inlineSize: '100%' },
                }}>
                {actions.map((action, i) => (
                  <div
                    key={`toolbar-action-${i}`}
                    css={{ inlineSize: '100%', '> *': { inlineSize: '100%' } }}>
                    {action}
                  </div>
                ))}
              </div>
            </EuiPopover>
          </EuiFlexItem>
        ) : (
          actions.map((action, i) => (
            <EuiFlexItem grow={false} key={`toolbar-action-${i}`}>
              {action}
            </EuiFlexItem>
          ))
        ))}
    </EuiFlexGroup>
  );
};

export default Toolbar;
